function getOrCreateCartRef() {
  let ref = localStorage.getItem('cartRef');
  if (!ref) {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    ref = Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
    localStorage.setItem('cartRef', ref);
  }
  return ref;
}

async function setCartAttribute(ref) {
  return fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      attributes: {
        'Cart Ref': ref
      }
    })
  })
    .then(res => res.json())
    .then(data => {
      console.log('✅ Cart Ref saved:', data.attributes['Cart Ref']);
      return data;
    });
}

function countSampleProducts(cartItems) {
  let sampleCount = 0;
  cartItems.forEach(item => {
    if (item.properties && item.properties.is_sample_product === 'true') {
      sampleCount += item.quantity;
    }
  });
  return sampleCount;
}

function updateCheckoutButtonState(cartItems) {
  // Handle both cart drawer and main cart page checkout buttons
  const checkoutButtons = document.querySelectorAll('[name="checkout"], .cart__checkout-button');
  if (checkoutButtons.length === 0) return;

  const sampleCount = countSampleProducts(cartItems);
  const isDisabled = sampleCount > 25;

  // Update checkout button state for all checkout buttons
  checkoutButtons.forEach(checkoutButton => {
    if (isDisabled) {
      checkoutButton.setAttribute('disabled', 'true');
      const spanElement = checkoutButton.querySelector('span');
      if (spanElement) {
        spanElement.textContent = `Too many samples (${sampleCount}/25)`;
      } else {
        // For buttons without span, update the text content directly
        checkoutButton.textContent = `Too many samples (${sampleCount}/25)`;
      }
    } else {
      checkoutButton.removeAttribute('disabled');
      const spanElement = checkoutButton.querySelector('span');
      if (spanElement) {
        spanElement.textContent = 'Checkout';
      } else {
        // For buttons without span, update the text content directly
        checkoutButton.textContent = 'Checkout';
      }
    }
  });

  // Show/hide samples warning based on disabled status
  // Handle both cart drawer and main cart page warnings
  const samplesWarnings = document.querySelectorAll('.samples_item--warning');
  samplesWarnings.forEach(warning => {
    if (isDisabled) {
      warning.style.setProperty('display', 'block', 'important');
    } else {
      warning.style.setProperty('display', 'none', 'important');
    }
  });
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    // Update checkout button state based on sample products
    if (parsedState.items) {
      updateCheckoutButtonState(parsedState.items);
    }

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    
      const checkoutButton = this.querySelector('[name="checkout"]');
      if (checkoutButton && !checkoutButton.hasAttribute('data-cart-ref-initialized')) {
        checkoutButton.setAttribute('data-cart-ref-initialized', 'true');
    
        checkoutButton.addEventListener('click', async (e) => {
          e.preventDefault();
    
          const cartRef = getOrCreateCartRef();
          try {
            await setCartAttribute(cartRef);
    
            // Optional: Show in drawer
            const refDisplay = this.querySelector('#CartRefDisplay');
            if (refDisplay) refDisplay.textContent = `Cart Ref: ${cartRef}`;
    
            window.location.href = '/checkout';
          } catch (err) {
            console.warn('❌ Could not set Cart Ref before checkout:', err);
            window.location.href = '/checkout'; // fail-safe
          }
        });
      }
    
      // Optional: Immediately show ref if available
      const refDisplay = this.querySelector('#CartRefDisplay');
      if (refDisplay) {
        refDisplay.textContent = `Cart Ref: ${getOrCreateCartRef()}`;
      }
    });    
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// Initial sample product check on page load
document.addEventListener('DOMContentLoaded', function() {
  fetch(`${routes.cart_url}.js`)
    .then(response => response.json())
    .then(cartData => {
      if (cartData.items) {
        updateCheckoutButtonState(cartData.items);
      }
    })
    .catch(error => console.error('Error fetching initial cart data:', error));
});
